import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Award, Download } from "lucide-react";

export const Route = createFileRoute("/_authenticated/student/certificates")({
  component: CertificatesPage,
});

type Certificate = {
  id: string; // enrollment id
  status: string;
  updated_at: string;
  courses: {
    name: string;
    description: string;
  } | null;
};

function CertificatesPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState<Certificate[]>([]);

  useEffect(() => {
    if (!user) return;
    const fetchCerts = async () => {
      const { data } = await supabase
        .from("enrollments")
        .select("id, status, updated_at, courses(name, description)")
        .eq("user_id", user.id)
        .eq("status", "completed");

      if (data) {
        setCertificates(data as any);
      }
      setLoading(false);
    };
    fetchCerts();
  }, [user]);

  if (loading) {
    return (
      <div className="flex h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-extrabold flex items-center gap-2">
          <Award className="h-6 w-6 text-primary" />
          شهاداتي
        </h1>
        <p className="text-muted-foreground">
          ألقِ نظرة على جميع إنجازاتك والشهادات التي حصلت عليها.
        </p>
      </div>

      {certificates.length === 0 ? (
        <Card className="rounded-3xl border-dashed">
          <CardContent className="p-12 flex flex-col items-center justify-center text-center">
            <Award className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-bold text-lg mb-1">لا توجد شهادات بعد</h3>
            <p className="text-muted-foreground text-sm">أكمل دوراتك لتحصل على شهادات موثقة هنا.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {certificates.map((cert) => (
            <Card
              key={cert.id}
              className="rounded-3xl border-0 shadow-soft overflow-hidden group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <CardContent className="p-6 flex flex-col items-center text-center relative">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 text-primary">
                  <Award className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-lg leading-tight mb-2">{cert.courses?.name}</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  تم الإكمال في: {new Date(cert.updated_at).toLocaleDateString("ar-EG")}
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="w-full rounded-full group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all"
                >
                  <Link to={`/certificate/${cert.id}`} target="_blank">
                    <Download className="h-4 w-4 me-2" /> عرض وتحميل الشهادة
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
